import { useState, useEffect, useRef, useContext } from "react";
import DigitalImg from "../../assets/audiobook.jpg";
import PrintImg from "../../assets/book.jpg";
import BundleImg from "../../assets/both.jpg";
import BosseImg from "../../assets/Bosse.jpg";
import AnnaImg from "../../assets/anna.jpg";
import { faStar } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import api from "../../api/apiClient";
import CreditModal from "../../components/CreditModal";
import { UserContext } from "../../components/UserContext";

// Återanvändbara format och röster
export const formats = [
  {
    label: "Digitalt",
    value: "digital",
    cost: 450,
    img: DigitalImg,
    description: "Lyssna på din berättelse som en ljudbok",
  },
  {
    label: "Tryckt",
    value: "print",
    cost: 750,
    img: PrintImg,
    description: "Få din berättelse som tryckt bok med hårda pärmar",
  },
  {
    label: "Hela paketet",
    value: "bundle",
    cost: 1000,
    img: BundleImg,
    description: "Lyssna och få din berättelse som tryckt bok",
  },
];

export const voices = [
  {
    label: "Anna",
    value: "l0KlHk6cDbVhBek0b7mF",
    avatar: AnnaImg,
    gradient: "linear-gradient(90deg, rgb(190, 56, 79), #f472b6, rgb(190, 56, 79))",
  },
  {
    label: "Bosse",
    value: "jO0xN2NygKrkV5PUEKgq",
    avatar: BosseImg,
    gradient: "linear-gradient(90deg, rgb(51, 101, 126), #347fa4, rgb(51, 101, 126))",
  },
];

const FormatCard = ({ label, image, description, selected, onClick, price, disabled }) => (
  <div
    onClick={!disabled ? onClick : () => alert("Du måste fylla på med stjärnor för att kunna skapa den här produkten.")}
    className={`p-4 rounded-lg transition-all duration-200 ${
      disabled ? "cursor-not-allowed opacity-40" : "cursor-pointer"
    } ${selected ? "scale-105 shadow-xl border-4 border-blue-500" : "shadow-sm border border-gray-200 bg-white"}`}
  >
    <img
      src={image}
      alt={label}
      className="w-full h-32 mx-auto object-cover mb-3 rounded-md"
    />
    <h3 className="text-lg font-medium text-center text-gray-800">{label}</h3>
    {description && <p className="text-sm text-center text-gray-500 mb-1">{description}</p>}
    {price != null && (
      <p className="text-xl md:text-sm text-center text-gray-600 mt-1">
        {price} <FontAwesomeIcon icon={faStar} className="text-softYellow" />
      </p>
    )}
  </div>
);

const VoiceCard = ({ label, image, selected, onClick, gradient, isPlaying }) => (
  <div
    onClick={onClick}
    className={`p-4 rounded-lg cursor-pointer transition-all duration-200 ${
      selected ? "scale-105 shadow-xl border-4 border-white" : "shadow-sm border border-transparent"
    }`}
    style={{ background: gradient, color: "white" }}
  >
    <img
      src={image}
      alt={label}
      className="w-20 h-20 mx-auto rounded-full object-cover mb-3 shadow-md"
    />
    <h3 className="text-lg font-medium text-center">
      {label}
      {isPlaying && <span className="ml-2 animate-pulse">🔊</span>}
    </h3>
  </div>
);

export const NarratorStep = ({
  data,
  onUpdate,
  onNext,
  voicePreviews,
  loadingVoices
}) => {
  const [playingVoice, setPlayingVoice] = useState(null);
  const [credits, setCredits] = useState(0);
  const [isModalOpen, setModalOpen] = useState(false);
  const [isGeneratingPreview, setIsGeneratingPreview] = useState(false);
  const currentAudioRef = useRef(null);
  const { currentUser } = useContext(UserContext);

  const fetchCredits = async () => {
    if (!currentUser?.id) return;
    try {
      const response = await api.get(`/api/get-credits?user_id=${currentUser.id}`);
      setCredits(response.data.credits || 0);
    } catch (err) {
      console.error("Kunde inte hämta credits:", err);
    }
  };

  useEffect(() => {
    fetchCredits();
  }, [currentUser]);

  const generateStaticSummary = () => {
    const { childName, childAge, gender, theme, interests } = data;
    const name = childName || "Robin";
    const pronoun = gender === "boy" ? "han" : "hon";
    const genderText = gender === "boy" ? "pojke" : "flicka";

    let summary = `Följ med ${name}`;
    summary += childAge ? `, en ${childAge} år gammal ${genderText}` : `, en ${genderText}`;
    summary += ` på ett magiskt äventyr.`;

    if (theme) summary += ` Berättelsen har temat "${theme.toLowerCase()}".`;
    if (interests) summary += ` ${name} tycker om att ${interests.toLowerCase()}.`;

    return summary;
  };

  const handleFormatClick = (value) => {
    const chosen = formats.find((f) => f.value === value);
    if (credits < chosen.cost) {
      alert("Du måste fylla på med stjärnor för att kunna skapa den här produkten.");
      return;
    }
    onUpdate("format", value);
    onUpdate("stars", chosen.cost);
  };

  const handleVoiceClick = async (voiceName) => {
    onUpdate("narrator", voiceName);
    setPlayingVoice(voiceName);
    setIsGeneratingPreview(true);

    // Stoppa eventuell tidigare uppspelning
    if (currentAudioRef.current) {
      currentAudioRef.current.pause();
      currentAudioRef.current = null;
    }

    try {
      const childName = data.childName || "dig";
      const voiceLabel = voices.find(v => v.value === voiceName)?.label || "din berättare";
      const miniStory = `Hej ${childName}! Jag heter ${voiceLabel} och jag kommer läsa din saga. Följ med på ett magiskt äventyr!`;


      // Generera ljud
      const audioRes = await api.post(
        "/generate-summary-audio",
        {
          summary: miniStory,
          voice_name: voiceName,
        },
        { responseType: "blob" }
      );

      const audioUrl = URL.createObjectURL(audioRes.data);
      const audio = new Audio(audioUrl);
      
      currentAudioRef.current = audio;
      audio.play();
      
      audio.onended = () => {
        setPlayingVoice(null);
      };
    } catch (error) {
      console.error("Error generating preview:", error);
      setPlayingVoice(null);
    } finally {
      setIsGeneratingPreview(false);
    }
  };

  const shouldShowNarrator = () =>
    data.format === "digital" || data.format === "bundle";

  if (loadingVoices) {
    return (
      <div className="p-4 text-center text-gray-600 animate-pulse">
        🔄 Laddar röster... Magin är på väg ✨
      </div>
    );
  }

  return (
    <div className="space-y-6 animate-fadeIn p-4">
      <div className="mb-4">
        <div className="flex justify-between items-center">
          <h2 className="text-2xl font-semibold">Välj format för boken</h2>
          <button
            onClick={() => setModalOpen(true)}
            className="bg-turquoise text-white px-3 py-1 rounded hover:bg-turquoise flex items-center space-x-2 transform transition-transform duration-200 hover:scale-110"
          >
            <FontAwesomeIcon icon={faStar} className="text-softYellow" />
            <span className="font-semibold">{credits}</span>
          </button>
        </div>
        <p className="text-sm text-gray-600 mt-2">
          Du kan fylla på med fler stjärnor genom att klicka på knappen här till höger. ✨
        </p>
        {credits < Math.min(...formats.map(f => f.cost)) && (
          <p className="text-sm text-red-500 mt-1">
            Just nu har du för få stjärnor för att skapa en bok. Magin väntar på dig – fyll på och fortsätt äventyret!
          </p>
        )}
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        {formats.map((f) => (
          <FormatCard
            key={f.value}
            label={f.label}
            image={f.img}
            description={f.description}
            selected={data.format === f.value}
            onClick={() => handleFormatClick(f.value)}
            price={f.cost}
            disabled={credits < f.cost}
          />
        ))}
      </div>

      {shouldShowNarrator() && (
        <>
          <h2 className="text-2xl font-semibold text-center mt-10">Välj en uppläsare</h2>
          <div className="grid grid-cols-2 gap-4">
            {voices.map((v) => (
              <VoiceCard
                key={v.value}
                label={v.label}
                image={v.avatar}
                selected={data.narrator === v.value}
                onClick={() => handleVoiceClick(v.value)}
                gradient={v.gradient}
                isPlaying={playingVoice === v.value && (isGeneratingPreview || true)}
              />
            ))}
          </div>
          {(playingVoice && isGeneratingPreview) && (
            <p className="text-center text-gray-600 animate-pulse">
              Skapar ljudförhandsvisning...
            </p>
          )}
        </>
      )}

      <div className="mt-8">
        <button
          type="submit"
          className={`w-full text-white py-2 rounded-lg hover:opacity-90 transition ${
            data.format && (data.format === "print" || data.narrator)
              ? ""
              : "opacity-50 cursor-not-allowed"
          }`}
          style={
            data.childName.trim() && data.childAge > 0 && data.gender
              ? { background: "linear-gradient(90deg,rgb(51, 101, 126), #347fa4, rgb(51, 101, 126))" }
              : { backgroundColor: "#ccc" }
          }
          disabled={!(data.format && (data.format === "print" || data.narrator))}
          onClick={onNext}
        >
          Nästa
        </button>
      </div>

      <CreditModal isOpen={isModalOpen} onClose={() => setModalOpen(false)} onPaymentSuccess={fetchCredits} />
    </div>
  );
};